import { useState, useEffect, useRef } from 'react';
import {
  Bot, Send, Trash2, Copy, Check, Sparkles, Zap, Globe,
  Settings, MessageSquare, Brain, HelpCircle, BarChart2,
  Plus, X, Save, RotateCcw, Eye, ChevronDown, ChevronUp,
  AlertTriangle, Clock, TrendingUp, DollarSign,
} from 'lucide-react';
import api from '../utils/api';
import API_BASE from '../utils/config';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

// ─── Sabitler ─────────────────────────────────────────────────────────────────

const MODELS = [
  { id: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5', badge: 'ÖNERİLEN', color: '#f59e0b', desc: 'Dengeli performans ve maliyet', speed: 'Orta', quality: 'Yüksek', inputPrice: 0.003, outputPrice: 0.015 },
  { id: 'claude-opus-4-5',   label: 'Claude Opus 4.5',   badge: 'PREMİUM',  color: '#8b5cf6', desc: 'En yüksek kalite ve anlayış',  speed: 'Yavaş', quality: 'En Yüksek', inputPrice: 0.015,  outputPrice: 0.075  },
  { id: 'claude-haiku-4-5',  label: 'Claude Haiku 4.5',  badge: 'EKONOMİK', color: '#22c55e', desc: 'Hızlı ve uygun maliyetli',     speed: 'Hızlı', quality: 'Orta',   inputPrice: 0.00025, outputPrice: 0.00125 },
  { id: 'gpt-4o',            label: 'GPT-4o',             badge: 'OPENAI',   color: '#10b981', desc: 'OpenAI\'nin amiral gemisi',    speed: 'Orta',  quality: 'Yüksek', inputPrice: 0.005,  outputPrice: 0.015  },
  { id: 'gpt-4o-mini',       label: 'GPT-4o Mini',        badge: 'EKONOMİK', color: '#06b6d4', desc: 'Hızlı ve ekonomik',           speed: 'Hızlı', quality: 'İyi',    inputPrice: 0.00015, outputPrice: 0.0006  },
  { id: 'gemini-pro',        label: 'Gemini Pro',         badge: 'GOOGLE',   color: '#3b82f6', desc: 'Google AI modeli',            speed: 'Hızlı', quality: 'Yüksek', inputPrice: 0.002,  outputPrice: 0.004  },
  { id: 'gemini-flash',      label: 'Gemini Flash',       badge: 'ÜCRETSİZ', color: '#6366f1', desc: 'Ücretsiz Google modeli',      speed: 'Çok Hızlı', quality: 'İyi', inputPrice: 0, outputPrice: 0 },
];

const TONES = [
  { id: 'friendly',     label: 'Samimi ve Sıcak',    emoji: '😊', example: '"Merhaba! 👋 Sizi görmek için sabırsızlanıyoruz!"' },
  { id: 'professional', label: 'Profesyonel',        emoji: '🎩', example: '"Merhaba. Size nasıl yardımcı olabiliriz? Tarih belirtiniz."' },
  { id: 'energetic',    label: 'Enerjik ve Modern',  emoji: '⚡', example: '"Hey! 🎉 Harika seçim! ✨ Tarih? 📅 Bakalım!"' },
];

const DEFAULT_SYSTEM_PROMPT = `# ROL VE KİMLİK
Sen {{hotel_name}} için rezervasyon asistanısın.
Adın: {{bot_name}}

# GÖREVLER
1. Soruları nazikçe yanıtla
2. Oda müsaitlik ve fiyat ver
3. Rezervasyon bilgilerini topla
   (ad, tarih, oda tipi, kişi sayısı)

# GÖRSELLER VE ODA FOTOĞRAFLARI
Müşteriye bir oda tanıtırken veya müşteri belirli bir odayı sorduğunda, cevabının sonuna veya uygun bir yere [PHOTO:Oda Adı] ekle (örn: [PHOTO:Deluxe Oda]). Sistem bu etiketi algılayarak müşteriye o odanın resimlerini otomatik olarak gönderecektir. Sadece mevcut odaların adlarını kullan.

# KURALLAR
- ASLA fiyat indirimi yapma
- Bilmediğin konularda:
  "Detay için {{phone}} arayabilirsiniz" de
- Rezervasyon onayı veremezsin, sadece topla

# OTEL BİLGİLERİ
{{hotel_info}}

# ÖRNEK KONUŞMALAR
{{conversation_examples}}

# SSS
{{faq_list}}`;

const VARIABLES = ['{{hotel_name}}', '{{bot_name}}', '{{phone}}', '{{email}}', '{{hotel_info}}', '{{conversation_examples}}', '{{faq_list}}'];

function calculateMonthlyCost(dailyMessages, model) {
  const m = MODELS.find(x => x.id === model);
  if (!m) return '0.00';
  const monthly = dailyMessages * 30;
  const avgTokens = 70;
  const totalTokens = monthly * avgTokens;
  const cost = (totalTokens / 1000) * (m.inputPrice + m.outputPrice);
  return cost.toFixed(2);
}

// ─── Alt Bileşenler ───────────────────────────────────────────────────────────

function TypingDots() {
  return (
    <div style={{ display: 'flex', gap: 4, padding: '12px 14px', background: 'white', borderRadius: '14px 14px 14px 4px', border: '1px solid #e2e8f0', width: 'fit-content' }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: '#6366f1', animation: 'pulse-dot 1.2s ease-in-out infinite', animationDelay: `${i * 0.2}s` }} />
      ))}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = '#6366f1', bg = '#ede9fe' }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 14, padding: '18px 20px', flex: 1, minWidth: 140 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={18} color={color} />
        </div>
        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color: '#1e293b', letterSpacing: '-0.02em' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ─── Ana Bileşen ──────────────────────────────────────────────────────────────

export default function BotControlPage() {
  const { hotel } = useAuth();
  const [activeTab, setActiveTab] = useState('2');
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Tab 1
  const [dailyMessages, setDailyMessages] = useState(50);

  // Tab 3
  const [promptExpanded, setPromptExpanded] = useState(false);

  // Tab 4
  const [examples, setExamples]         = useState([]);
  const [newExample, setNewExample]      = useState({ customerQuestion: '', botResponse: '' });
  const [addingExample, setAddingExample] = useState(false);

  // Tab 5
  const [faqs, setFaqs]         = useState([]);
  const [faqSearch, setFaqSearch] = useState('');
  const [newFaq, setNewFaq]       = useState({ question: '', answer: '', category: '' });
  const [addingFaq, setAddingFaq]  = useState(false);
  const [editFaq, setEditFaq]     = useState(null);

  // Tab 6 (test)
  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState('');
  const [sending, setSending]     = useState(false);
  const [lastUsage, setLastUsage] = useState(null);
  const messagesEndRef = useRef(null);

  // Tab 7
  const [stats, setStats]    = useState(null);
  const [statPeriod, setStatPeriod] = useState('7');

  // ─── Yükle ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (activeTab === '5') loadStats();
  }, [activeTab, statPeriod]);


  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadAll() {
    setLoading(true);
    try {
      const [s, ex, fq, history] = await Promise.all([
        api.get('/bot/settings'),
        api.get('/bot/examples'),
        api.get('/hotels/faqs'),
        api.get('/bot/test/history'),
      ]);
      setSettings(s);
      setExamples(ex);
      setFaqs(fq);
      setMessages(history.messages || []);
    } catch { toast.error('Veriler yüklenemedi'); }
    finally { setLoading(false); }
  }

  async function loadStats() {
    try {
      const s = await api.get(`/bot/stats?period=${statPeriod}`);
      setStats(s);
    } catch {}
  }

  // ─── Kaydet ────────────────────────────────────────────────────────────────

  async function saveSettings(partial) {
    setSaving(true);
    try {
      const updated = await api.put('/bot/settings', { ...settings, ...partial });
      setSettings(updated);
      toast.success('Kaydedildi');
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSaving(false); }
  }

  function updateLocal(key, value) {
    setSettings(prev => ({ ...prev, [key]: value }));
  }

  async function resetSettings() {
    if (!window.confirm('Tüm bot ayarları varsayılana sıfırlanacak. Emin misiniz?')) return;
    setSaving(true);
    try {
      const s = await api.post('/bot/settings/reset');
      setSettings(s);
      toast.success('Varsayılana sıfırlandı');
    } catch { toast.error('Sıfırlanamadı'); }
    finally { setSaving(false); }
  }

  // ─── Örnekler ──────────────────────────────────────────────────────────────

  async function addExample() {
    if (!newExample.customerQuestion.trim() || !newExample.botResponse.trim()) {
      toast.error('Soru ve cevap gerekli');
      return;
    }
    try {
      const ex = await api.post('/bot/examples', newExample);
      setExamples(prev => [...prev, ex]);
      setNewExample({ customerQuestion: '', botResponse: '' });
      setAddingExample(false);
      toast.success('Örnek eklendi');
    } catch { toast.error('Eklenemedi'); }
  }

  async function deleteExample(id) {
    try {
      await api.delete(`/bot/examples/${id}`);
      setExamples(prev => prev.filter(e => e.id !== id));
      toast.success('Silindi');
    } catch { toast.error('Silinemedi'); }
  }

  // ─── SSS ───────────────────────────────────────────────────────────────────

  async function addFaq() {
    if (!newFaq.question.trim() || !newFaq.answer.trim()) {
      toast.error('Soru ve cevap gerekli');
      return;
    }
    try {
      const faq = await api.post('/hotels/faqs', newFaq);
      setFaqs(prev => [...prev, faq]);
      setNewFaq({ question: '', answer: '', category: '' });
      setAddingFaq(false);
      toast.success('SSS eklendi');
    } catch { toast.error('Eklenemedi'); }
  }

  async function saveFaqEdit() {
    try {
      await api.put(`/hotels/faqs/${editFaq.id}`, editFaq);
      setFaqs(prev => prev.map(f => f.id === editFaq.id ? editFaq : f));
      setEditFaq(null);
      toast.success('Kaydedildi');
    } catch { toast.error('Kaydedilemedi'); }
  }

  async function deleteFaq(id) {
    try {
      await api.delete(`/hotels/faqs/${id}`);
      setFaqs(prev => prev.filter(f => f.id !== id));
      toast.success('Silindi');
    } catch { toast.error('Silinemedi'); }
  }

  const filteredFaqs = faqs.filter(f =>
    !faqSearch || f.question.toLowerCase().includes(faqSearch.toLowerCase()) || f.answer.toLowerCase().includes(faqSearch.toLowerCase())
  );

  // ─── Test ──────────────────────────────────────────────────────────────────

  async function handleSend(text) {
    const msg = (text || input).trim();
    if (!msg) return;
    setInput('');
    setSending(true);
    setMessages(prev => [...prev, { id: Date.now(), role: 'USER', content: msg, sentAt: new Date().toISOString() }]);
    const start = Date.now();
    try {
      const data = await api.post('/bot/test', { message: msg });
      const ms = Date.now() - start;
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'ASSISTANT', content: data.reply, images: data.images, sentAt: new Date().toISOString() }]);
      setLastUsage({ responseMs: ms, model: settings?.aiModel || 'gpt-4o-mini' });
      if (data.hasReservation) toast.success('Bot rezervasyon talebi oluşturdu!', { icon: '✅' });
    } catch { toast.error('Bot yanıt veremedi'); }
    finally { setSending(false); }
  }

  async function handleReset() {
    try {
      await api.delete('/bot/test/reset');
      setMessages([]);
      setLastUsage(null);
      toast.success('Konuşma sıfırlandı');
    } catch { toast.error('Sıfırlanamadı'); }
  }

  const quickMessages = [
    { text: 'Fiyatlarınız ne kadar?', emoji: '💰' },
    { text: 'Bu hafta sonu müsait oda var mı?', emoji: '📅' },
    { text: 'Rezervasyon yapmak istiyorum', emoji: '📝' },
    { text: "Booking.com'da daha ucuz gördüm", emoji: '🔥' },
    { text: 'Hello, I need a room for 2 nights', emoji: '🇬🇧' },
  ];

  // ─── Tabs Tanımı ───────────────────────────────────────────────────────────

  const tabs = [
    { id: '2', label: 'Botunu Tanımla',  icon: Sparkles },
    { id: '3', label: 'SSS',             icon: HelpCircle },
    { id: '4', label: 'Canlı Test',      icon: Zap },
    { id: '5', label: 'İstatistik',      icon: BarChart2 },
  ];

  if (loading || !settings) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 12, color: '#64748b' }}>
        <div style={{ width: 48, height: 48, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <span style={{ fontSize: 14 }}>Bot ayarları yükleniyor...</span>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: 28, height: '100%', display: 'flex', flexDirection: 'column', gap: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 16, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(99,102,241,0.4)' }}>
            <Bot size={24} color="white" />
          </div>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: '#1e293b' }}>AI Asistan Kontrolü</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                Claude Haiku 4.5 ile çalışıyor
              </span>
            </div>
          </div>
        </div>
        <button onClick={resetSettings} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: 13, cursor: 'pointer' }}>
          <RotateCcw size={14} /> Varsayılana Sıfırla
        </button>
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', background: 'white', borderRadius: 14, padding: 4, border: '1px solid #e2e8f0', marginBottom: 20, gap: 2, overflowX: 'auto', flexShrink: 0 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', border: 'none', borderRadius: 10, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
            background: activeTab === t.id ? 'linear-gradient(135deg, #6366f1, #3b82f6)' : 'transparent',
            color:      activeTab === t.id ? 'white' : '#64748b',
            fontWeight: activeTab === t.id ? 700 : 500,
            boxShadow:  activeTab === t.id ? '0 2px 8px rgba(99,102,241,0.3)' : 'none',
            transition: 'all 0.2s',
          }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab İçerikleri */}
      <div style={{ flex: 1, overflow: 'auto' }}>

        {/* ── TAB 2: Botunu Tanımla (birleşik) ── */}
        {activeTab === '2' && (
          <div style={{ maxWidth: 960 }}>

            {/* 1. Bot Kimliği */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, marginBottom: 20, alignItems: 'start' }}>
              <div className="card">
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 16 }}>1. Bot Kimliği</div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Bot Adı</label>
                <input
                  value={settings.botName || ''}
                  onChange={e => updateLocal('botName', e.target.value)}
                  placeholder="Örnek: Aksoy Otel Asistanı"
                  style={{ borderRadius: 8, border: '1.5px solid #e2e8f0', padding: '10px 12px', fontSize: 13, width: '100%' }}
                />
              </div>
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '14px 16px', fontSize: 13, color: '#78350f', lineHeight: 1.65 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>💡 Bot Adı</div>
                Bu isim müşterinin gördüğü bot ismidir. Otelinizin kişiliğini yansıtsın.
                <div style={{ marginTop: 10, background: 'white', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#92400e' }}>
                  <strong>Örnekler:</strong><br />"Aksoy Otel Asistanı"<br />"Ada" · "Bozcaada"
                </div>
              </div>
            </div>

            {/* 2. Konuşma Tonu */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, marginBottom: 20, alignItems: 'start' }}>
              <div className="card">
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 14 }}>2. Konuşma Tonu</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {TONES.map(t => (
                    <label key={t.id} style={{
                      flex: 1, padding: '14px', borderRadius: 12, cursor: 'pointer', textAlign: 'center',
                      border: `2px solid ${settings.tone === t.id ? '#6366f1' : '#e2e8f0'}`,
                      background: settings.tone === t.id ? '#ede9fe' : 'white',
                    }}>
                      <input type="radio" name="tone" value={t.id} checked={settings.tone === t.id} onChange={() => updateLocal('tone', t.id)} style={{ display: 'none' }} />
                      <div style={{ fontSize: 22, marginBottom: 6 }}>{t.emoji}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: settings.tone === t.id ? '#6366f1' : '#374151', marginBottom: 8 }}>{t.label}</div>
                      <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic', lineHeight: 1.4 }}>{t.example}</div>
                    </label>
                  ))}
                </div>
                <div style={{ marginTop: 14, borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Emoji Kullanımı</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#6366f1' }}>%{settings.emojiFrequency}</span>
                  </div>
                  <input type="range" min={0} max={100} step={10} value={settings.emojiFrequency} onChange={e => updateLocal('emojiFrequency', +e.target.value)} style={{ width: '100%', accentColor: '#6366f1', marginBottom: 8 }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94a3b8', marginBottom: 10 }}>
                    <span>Hiç Yok (0%)</span><span>Az (30%)</span><span>Çok (100%)</span>
                  </div>
                  <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 8, fontSize: 13, color: '#374151' }}>
                    Önizleme: {settings.emojiFrequency === 0 ? 'Merhaba! Size nasıl yardımcı olabilirim?' : settings.emojiFrequency <= 30 ? 'Merhaba! Size nasıl yardımcı olabilirim? 🙂' : 'Merhaba! 👋 Size nasıl yardımcı olabilirim? 😊 Harika seçim! ✨'}
                  </div>
                </div>
              </div>
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '14px 16px', fontSize: 13, color: '#78350f', lineHeight: 1.65 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>💡 Konuşma Tonu</div>
                Botun genel ses tonunu ve hitap şeklini belirler. Daha ince davranış ayarlarını "Bot Karakteri" alanına yazabilirsiniz.
              </div>
            </div>

            {/* 3. Bot Karakteri */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, marginBottom: 20, alignItems: 'start' }}>
              <div className="card">
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>3. Bot Karakteri ve Davranışları</div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>Botunuzun nasıl konuşmasını, nasıl davranmasını istediğinizi kendi cümlelerinizle anlatın.</div>
                <textarea
                  value={settings.botCharacter || ''}
                  onChange={e => updateLocal('botCharacter', e.target.value)}
                  rows={8}
                  placeholder="Örnek: Samimi ama profesyonel bir dil kullan. Müşteriye 'Bey/Hanım' diye hitap et. Asla agresif satış yapma, doğal bir sohbet havası olsun..."
                  style={{ width: '100%', borderRadius: 10, border: '1.5px solid #e2e8f0', padding: '10px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
                />
              </div>
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '14px 16px', fontSize: 13, color: '#78350f', lineHeight: 1.65 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>💡 Nasıl Yazmalı?</div>
                Bunu doğal dilde yazın — tıpkı bir çalışana yaptığı işi anlatır gibi.
                <div style={{ marginTop: 10, background: 'white', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#92400e' }}>
                  <strong>Örnek:</strong><br />
                  "Samimi ama profesyonel bir dil kullan. Müşteriye 'Bey/Hanım' diye hitap et. Asla agresif satış yapma, doğal bir sohbet havası olsun."
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: '#92400e' }}>Bunu yazarsan bot tam bu şekilde konuşmaya başlar.</div>
                <button
                  onClick={() => updateLocal('botCharacter', 'Samimi ama profesyonel bir dil kullan. Müşteriye "Bey/Hanım" diye hitap et. Asla agresif satış yapma, doğal bir sohbet havası olsun. Otel hakkında bilmediğin bir soru gelirse dürüstçe "Bu konuda sizi aramamız gerekiyor, telefonu arar mısınız?" de.')}
                  style={{ marginTop: 10, width: '100%', padding: '7px', borderRadius: 8, border: '1px solid #fcd34d', background: 'white', color: '#92400e', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                >
                  📋 Örnek Şablonu Kullan
                </button>
              </div>
            </div>

            {/* 4. Karşılama Mesajı */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, marginBottom: 20, alignItems: 'start' }}>
              <div className="card">
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10 }}>4. Karşılama Mesajı</div>
                <textarea
                  value={settings.welcomeMessage || ''}
                  onChange={e => updateLocal('welcomeMessage', e.target.value)}
                  rows={4}
                  maxLength={500}
                  placeholder="Müşteri ilk mesaj attığında gönderilecek karşılama mesajı..."
                  style={{ width: '100%', borderRadius: 10, border: '1.5px solid #e2e8f0', padding: '10px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
                />
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{(settings.welcomeMessage || '').length} / 500 karakter</div>
              </div>
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '14px 16px', fontSize: 13, color: '#78350f', lineHeight: 1.65 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>💡 Karşılama Mesajı</div>
                Müşteri ilk mesajı attığında bot bu mesajla karşılık verir. İyi bir karşılama güven verir.
                <div style={{ marginTop: 10, background: 'white', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#92400e' }}>
                  <strong>Örnek:</strong><br />
                  "Merhaba! Aksoy Otel'e hoş geldiniz. 👋 Size nasıl yardımcı olabilirim?"
                </div>
              </div>
            </div>

            {/* 5. Özel Bilgiler */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, marginBottom: 20, alignItems: 'start' }}>
              <div className="card">
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>5. Botun Bildiği Özel Bilgiler</div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>Sadece sizin otelinize özel, bilinmesi gereken bilgileri yazın.</div>
                <textarea
                  value={settings.customKnowledge || ''}
                  onChange={e => updateLocal('customKnowledge', e.target.value)}
                  rows={6}
                  placeholder={'- En iyi restoran: Sunset Restaurant, 19:00-23:00\n- Gizli plaj: Otel çıkışından 5 dk. yürüme\n- Bisiklet kiralama: Resepsiyondan ücretsiz\n- Evcil hayvan: Sadece küçük ırklar (maks 5 kg)'}
                  style={{ width: '100%', borderRadius: 10, border: '1.5px solid #e2e8f0', padding: '10px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
                />
              </div>
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '14px 16px', fontSize: 13, color: '#78350f', lineHeight: 1.65 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>💡 Özel Bilgiler</div>
                Bot zaten otel genel bilgilerini (odalar, fiyatlar) biliyor. Buraya "standart" olmayan, otelinize özgü detayları ekleyin.
                <div style={{ marginTop: 10, background: 'white', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#92400e' }}>
                  <strong>Örnekler:</strong><br />
                  • Transfer hizmeti saatleri<br />
                  • Yakın çevredeki aktiviteler<br />
                  • Ücretsiz sunduğunuz ekstralar<br />
                  • Evcil hayvan / sigara politikası
                </div>
              </div>
            </div>

            {/* 6. Özel Durum Yanıtları */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, marginBottom: 20, alignItems: 'start' }}>
              <div className="card">
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 16 }}>6. Özel Durum Yanıtları</div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>💬 Fiyat İtirazı ("Booking'de ucuz gördüm" derse)</label>
                  <textarea value={settings.priceObjectionResponse || ''} onChange={e => updateLocal('priceObjectionResponse', e.target.value)} rows={3} placeholder='Örn: "Anlıyorum. Booking.com komisyon aldığı için fiyatlar farklı. Direkt bizden rezervasyon yaparsanız %5 indirim uygulayabiliriz! 🙂"' style={{ width: '100%', borderRadius: 10, border: '1.5px solid #e2e8f0', padding: '10px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>🤷 Cevap Verememe Durumu</label>
                  <textarea value={settings.noAnswerMessage || ''} onChange={e => updateLocal('noAnswerMessage', e.target.value)} rows={2} placeholder='Örn: "Üzgünüm, bu konuda yardımcı olamıyorum. 😔 Sizi temsilcimize bağlıyorum!"' style={{ width: '100%', borderRadius: 10, border: '1.5px solid #e2e8f0', padding: '10px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
                </div>
              </div>
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '14px 16px', fontSize: 13, color: '#78350f', lineHeight: 1.65 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>💡 Özel Durum Yanıtları</div>
                Botun bazı durumlarda ne cevap vereceğini önceden tanımlayın. Tanımlamazsanız bot kendi uydurabilir.
                <div style={{ marginTop: 10, background: 'white', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#92400e' }}>
                  <strong>Fiyat itirazı için öneri:</strong><br />
                  "Anlıyorum. Booking.com'dan farklı olarak bizden direkt rezervasyon yapınca daha avantajlı oluyor."
                </div>
              </div>
            </div>

            {/* 7. Mesai Dışı */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, marginBottom: 20, alignItems: 'start' }}>
              <div className="card">
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 14 }}>7. Mesai Dışı Modu</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={settings.afterHoursEnabled} onChange={e => updateLocal('afterHoursEnabled', e.target.checked)} style={{ accentColor: '#6366f1', width: 16, height: 16 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: settings.afterHoursEnabled ? '#6366f1' : '#64748b' }}>Mesai Dışı Modunu Aktif Et</span>
                </label>
                {settings.afterHoursEnabled && (
                  <div>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                      <div>
                        <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>Başlangıç</label>
                        <input type="time" value={settings.afterHoursStart || '23:00'} onChange={e => updateLocal('afterHoursStart', e.target.value)} style={{ borderRadius: 8, border: '1.5px solid #e2e8f0', padding: '6px 10px', fontSize: 13 }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>Bitiş</label>
                        <input type="time" value={settings.afterHoursEnd || '08:00'} onChange={e => updateLocal('afterHoursEnd', e.target.value)} style={{ borderRadius: 8, border: '1.5px solid #e2e8f0', padding: '6px 10px', fontSize: 13 }} />
                      </div>
                    </div>
                    <textarea value={settings.afterHoursMessage || ''} onChange={e => updateLocal('afterHoursMessage', e.target.value)} rows={2} placeholder='Örn: "Merhaba! 🌙 Şu an mesai dışıyız. Sabah 08:00de dönüş yapacağız! ☀️"' style={{ width: '100%', borderRadius: 10, border: '1.5px solid #e2e8f0', padding: '10px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
                  </div>
                )}
              </div>
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '14px 16px', fontSize: 13, color: '#78350f', lineHeight: 1.65 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>💡 Mesai Dışı Modu</div>
                Bot 7/24 çalışır, ama mesai dışında insanları beklentiye sokmamak için ayrı bir mesaj gösterilebilir.
                <div style={{ marginTop: 10, background: 'white', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#92400e' }}>
                  <strong>Örnek:</strong><br />
                  "Merhaba! 🌙 Şu an mesai dışıyız. Sabah 08:00'de dönüş yapacağız! ☀️"
                </div>
              </div>
            </div>

            {/* 8. Konuşma Örnekleri */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, marginBottom: 20, alignItems: 'start' }}>
              <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>8. Özel Konuşma Örnekleri</div>
                  <button onClick={() => setAddingExample(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #6366f1, #3b82f6)', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    <Plus size={14} /> Yeni Örnek
                  </button>
                </div>
                {addingExample && (
                  <div style={{ marginBottom: 12, border: '2px solid #c4b5fd', borderRadius: 10, padding: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', marginBottom: 12 }}>Yeni Örnek Ekle</div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Müşteri Sorusu</label>
                    <input value={newExample.customerQuestion} onChange={e => setNewExample(p => ({ ...p, customerQuestion: e.target.value }))} placeholder="Örn: Havuz var mı?" style={{ width: '100%', borderRadius: 8, border: '1.5px solid #e2e8f0', padding: '8px 10px', fontSize: 13, marginBottom: 10 }} />
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Bot Cevabı</label>
                    <textarea value={newExample.botResponse} onChange={e => setNewExample(p => ({ ...p, botResponse: e.target.value }))} rows={3} placeholder="Örn: Evet! Açık havuzumuz var. 🏊 Mayıs-Ekim: 10:00-19:00" style={{ width: '100%', borderRadius: 8, border: '1.5px solid #e2e8f0', padding: '8px 10px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', marginBottom: 10 }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={addExample} style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#6366f1', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Ekle</button>
                      <button onClick={() => setAddingExample(false)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: 13, cursor: 'pointer' }}>İptal</button>
                    </div>
                  </div>
                )}
                {examples.length === 0 && !addingExample && (
                  <div style={{ textAlign: 'center', padding: '30px 20px', color: '#94a3b8', background: '#f8fafc', borderRadius: 12 }}>
                    <Brain size={28} color="#c4b5fd" style={{ marginBottom: 8 }} />
                    <div style={{ fontWeight: 700, color: '#475569', marginBottom: 4 }}>Henüz örnek yok</div>
                    <div style={{ fontSize: 13 }}>Örnekler botun otel hakkındaki özel sorulara daha iyi cevap vermesini sağlar.</div>
                  </div>
                )}
                {examples.map((ex, i) => (
                  <div key={ex.id} style={{ padding: 12, border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 8 }}>Örnek #{i + 1}</div>
                      <button onClick={() => deleteExample(ex.id)} style={{ padding: '4px 8px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={13} /></button>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>👤 Müşteri:</div>
                    <div style={{ fontSize: 13, color: '#1e293b', padding: '6px 10px', background: '#f8fafc', borderRadius: 8, marginBottom: 8 }}>{ex.customerQuestion}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>🤖 Bot:</div>
                    <div style={{ fontSize: 13, color: '#1e293b', padding: '6px 10px', background: '#f0fdf4', borderRadius: 8 }}>{ex.botResponse}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '14px 16px', fontSize: 13, color: '#78350f', lineHeight: 1.65 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>💡 Konuşma Örnekleri</div>
                Bot bu örneklerden öğrenerek benzer sorulara aynı tarzda cevap verir. Otelinize özel, kritik soruları ekleyin.
                <div style={{ marginTop: 10, background: 'white', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#92400e' }}>
                  <strong>Eklenebilecek örnekler:</strong><br />
                  • "Evcil hayvan kabul ediyor musunuz?"<br />
                  • "Engelli odanız var mı?"<br />
                  • "Gece gürültü kuralı var mı?"
                </div>
              </div>
            </div>

            {/* İleri Düzey: System Prompt */}
            <div className="card" style={{ marginBottom: 20 }}>
              <button
                onClick={() => setPromptExpanded(p => !p)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertTriangle size={15} color="#f59e0b" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>İleri Düzey: System Prompt Editörü</span>
                  <span style={{ fontSize: 11, color: '#94a3b8', background: '#f1f5f9', padding: '2px 8px', borderRadius: 999 }}>Teknik</span>
                </div>
                {promptExpanded ? <ChevronUp size={16} color="#64748b" /> : <ChevronDown size={16} color="#64748b" />}
              </button>
              {promptExpanded && (
                <div style={{ marginTop: 14, borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                  <div style={{ padding: '10px 14px', background: '#fef3c7', borderRadius: 10, border: '1px solid #fcd34d', display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#92400e', marginBottom: 14 }}>
                    <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                    Bu alan ileri kullanıcılar içindir. Yanlış yapılandırma botu bozabilir.
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <button onClick={() => updateLocal('systemPrompt', DEFAULT_SYSTEM_PROMPT)} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: 12, cursor: 'pointer' }}>
                      Varsayılana Sıfırla
                    </button>
                  </div>
                  <textarea
                    value={settings.systemPrompt || DEFAULT_SYSTEM_PROMPT}
                    onChange={e => updateLocal('systemPrompt', e.target.value)}
                    rows={15}
                    style={{ width: '100%', borderRadius: 10, border: '1.5px solid #e2e8f0', padding: '12px', fontSize: 12, fontFamily: '"Fira Code","Cascadia Code",monospace', resize: 'vertical', lineHeight: 1.7, color: '#1e293b' }}
                  />
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 8, letterSpacing: '0.05em' }}>KULLANILABILIR DEĞİŞKENLER</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {VARIABLES.map(v => (
                        <code key={v} onClick={() => { navigator.clipboard.writeText(v); toast.success('Kopyalandı'); }} style={{ padding: '3px 8px', background: '#f1f5f9', borderRadius: 6, fontSize: 11, cursor: 'pointer', color: '#6366f1', fontFamily: 'monospace' }}>{v}</code>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button onClick={() => saveSettings({})} disabled={saving} style={{ padding: '12px 24px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #6366f1, #3b82f6)', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Save size={16} /> {saving ? 'Kaydediliyor...' : 'Tüm Ayarları Kaydet'}
            </button>
          </div>
        )}

        {/* ── TAB 3: SSS ── */}
        {activeTab === '3' && (
          <div style={{ maxWidth: 800 }}>
            {/* SSS Açıklama Bloğu */}
            <div style={{ background: 'linear-gradient(135deg, #eff6ff, #f0fdf4)', border: '1px solid #bfdbfe', borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <HelpCircle size={17} color="white" />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 4 }}>💡 SSS Nedir, Nasıl Çalışır?</div>
                  <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7 }}>
                    Müşterilerinizin sık sorduğu soruları ve bunlara vereceğiniz <strong>kesin cevapları</strong> buraya yazın.
                    Bot, bu soruları gördüğünde <strong>SİZİN yazdığınız cevabı kullanır</strong>, kendi tahmin etmez.
                    Bu sayede yanlış bilgi verme riski ortadan kalkar.
                  </div>
                </div>
              </div>
              <div style={{ background: 'white', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#475569', lineHeight: 1.9, border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>Örnek SSS'ler:</div>
                <div><span style={{ color: '#6366f1', fontWeight: 600 }}>S:</span> "Kahvaltı dahil mi?" &nbsp;<span style={{ color: '#94a3b8' }}>→</span>&nbsp; <span style={{ color: '#6366f1', fontWeight: 600 }}>C:</span> "Evet, tüm odalarımızda açık büfe kahvaltı ücrete dahildir, saat 07:00–10:30 arası."</div>
                <div><span style={{ color: '#6366f1', fontWeight: 600 }}>S:</span> "Evcil hayvan kabul ediyor musunuz?" &nbsp;<span style={{ color: '#94a3b8' }}>→</span>&nbsp; <span style={{ color: '#6366f1', fontWeight: 600 }}>C:</span> "Üzgünüz, evcil hayvan kabul edilmemektedir."</div>
                <div><span style={{ color: '#6366f1', fontWeight: 600 }}>S:</span> "Check-in / check-out saatleri nedir?" &nbsp;<span style={{ color: '#94a3b8' }}>→</span>&nbsp; <span style={{ color: '#6366f1', fontWeight: 600 }}>C:</span> "Giriş 14:00, çıkış 12:00'dir. Erken giriş/geç çıkış için lütfen bizimle iletişime geçin."</div>
                <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
                  Ne kadar çok soru-cevap eklerseniz, bot o kadar doğru ve güvenilir cevaplar verir.
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
              <input
                value={faqSearch}
                onChange={e => setFaqSearch(e.target.value)}
                placeholder="SSS ara..."
                style={{ flex: 1, borderRadius: 10, border: '1.5px solid #e2e8f0', padding: '10px 14px', fontSize: 13 }}
              />
              <button onClick={() => setAddingFaq(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #6366f1, #3b82f6)', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <Plus size={14} /> Yeni SSS
              </button>
            </div>

            {addingFaq && (
              <div className="card" style={{ marginBottom: 14, border: '2px solid #c4b5fd' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', marginBottom: 12 }}>Yeni SSS Ekle</div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Kategori (opsiyonel)</label>
                <input value={newFaq.category} onChange={e => setNewFaq(p => ({ ...p, category: e.target.value }))} placeholder="Örn: Genel, Odalar, Hizmetler" style={{ width: '100%', borderRadius: 8, border: '1.5px solid #e2e8f0', padding: '8px 10px', fontSize: 13, marginBottom: 10 }} />
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Soru</label>
                <input value={newFaq.question} onChange={e => setNewFaq(p => ({ ...p, question: e.target.value }))} placeholder="Örn: WiFi var mı?" style={{ width: '100%', borderRadius: 8, border: '1.5px solid #e2e8f0', padding: '8px 10px', fontSize: 13, marginBottom: 10 }} />
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Cevap</label>
                <textarea value={newFaq.answer} onChange={e => setNewFaq(p => ({ ...p, answer: e.target.value }))} rows={3} placeholder="Cevabı yazın..." style={{ width: '100%', borderRadius: 8, border: '1.5px solid #e2e8f0', padding: '8px 10px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', marginBottom: 10 }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={addFaq} style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#6366f1', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Ekle</button>
                  <button onClick={() => setAddingFaq(false)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: 13, cursor: 'pointer' }}>İptal</button>
                </div>
              </div>
            )}

            {filteredFaqs.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8', background: 'white', borderRadius: 14, border: '1.5px dashed #e2e8f0' }}>
                <HelpCircle size={32} color="#c4b5fd" style={{ marginBottom: 10 }} />
                <div style={{ fontWeight: 700, color: '#475569', marginBottom: 4 }}>{faqSearch ? 'Sonuç bulunamadı' : 'Henüz SSS yok'}</div>
              </div>
            )}

            {filteredFaqs.map(faq => (
              <div key={faq.id} className="card" style={{ marginBottom: 8 }}>
                {editFaq?.id === faq.id ? (
                  <div>
                    <input value={editFaq.question} onChange={e => setEditFaq(p => ({ ...p, question: e.target.value }))} style={{ width: '100%', borderRadius: 8, border: '1.5px solid #c4b5fd', padding: '8px 10px', fontSize: 13, marginBottom: 8, fontWeight: 600 }} />
                    <textarea value={editFaq.answer} onChange={e => setEditFaq(p => ({ ...p, answer: e.target.value }))} rows={3} style={{ width: '100%', borderRadius: 8, border: '1.5px solid #e2e8f0', padding: '8px 10px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', marginBottom: 8 }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={saveFaqEdit} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#6366f1', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Kaydet</button>
                      <button onClick={() => setEditFaq(null)} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: 12, cursor: 'pointer' }}>İptal</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      {faq.category && <span style={{ fontSize: 10, fontWeight: 700, color: '#6366f1', background: '#ede9fe', padding: '2px 7px', borderRadius: 999, marginBottom: 6, display: 'inline-block' }}>{faq.category}</span>}
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b', marginBottom: 4 }}>{faq.question}</div>
                      <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>{faq.answer}</div>
                      {faq.askedCount > 0 && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>📊 {faq.askedCount} kez soruldu</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button onClick={() => setEditFaq({ ...faq })} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', color: '#6366f1', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                      <button onClick={() => deleteFaq(faq.id)} style={{ padding: '6px 10px', borderRadius: 8, border: 'none', background: '#fef2f2', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={13} /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── TAB 4: Canlı Test ── */}
        {activeTab === '4' && (
          <div style={{ display: 'flex', gap: 20, height: 600 }}>
            {/* Chat */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'white', borderRadius: 18, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, #fafaff, white)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>Test Konuşması</span>
                  {messages.length > 0 && <span style={{ fontSize: 11, color: '#94a3b8', background: '#f1f5f9', borderRadius: 999, padding: '2px 8px' }}>{messages.length} mesaj</span>}
                </div>
                <button onClick={handleReset} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, background: '#fef2f2', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  <Trash2 size={12} /> Sıfırla
                </button>
              </div>

              <div style={{ flex: 1, overflow: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10, background: '#fafbff' }}>
                {messages.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#94a3b8', margin: 'auto', padding: 40 }}>
                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #ede9fe, #dbeafe)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                      <Bot size={28} color="#6366f1" />
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>Bot hazır!</div>
                    <div style={{ fontSize: 12 }}>Sağdaki senaryolardan birini seçin ya da kendi sorunuzu yazın</div>
                  </div>
                )}
                {messages.map(msg => (
                  <div key={msg.id} style={{ display: 'flex', justifyContent: msg.role === 'USER' ? 'flex-end' : 'flex-start' }}>
                    {msg.role === 'ASSISTANT' && (
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 6, flexShrink: 0, alignSelf: 'flex-end' }}>
                        <Bot size={13} color="white" />
                      </div>
                    )}
                    <div style={{
                      maxWidth: '72%', padding: '10px 14px',
                      borderRadius: msg.role === 'USER' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      background: msg.role === 'USER' ? 'linear-gradient(135deg, #6366f1, #3b82f6)' : 'white',
                      color: msg.role === 'USER' ? 'white' : '#1e293b',
                      fontSize: 13, lineHeight: 1.6,
                      boxShadow: msg.role === 'USER' ? '0 2px 8px rgba(99,102,241,0.3)' : '0 1px 4px rgba(0,0,0,0.06)',
                      border: msg.role === 'ASSISTANT' ? '1px solid #f1f5f9' : 'none',
                    }}>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                      {(() => {
                        const imgs = Array.isArray(msg.images)
                          ? msg.images
                          : (() => { try { return JSON.parse(msg.images || '[]'); } catch { return []; } })();
                        if (imgs.length === 0) return null;
                        return (
                          <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {imgs.map((imgUrl, idx) => {
                              let finalUrl = imgUrl;
                              try {
                                if (finalUrl.includes('ngrok-free.app')) {
                                  finalUrl = `${API_BASE}${new URL(imgUrl).pathname}`;
                                } else if (!finalUrl.startsWith('http')) {
                                  finalUrl = `${API_BASE}${finalUrl}`;
                                }
                              } catch (e) {}
                              return <img key={idx} src={finalUrl} alt="Oda Görseli" style={{ width: 140, height: 90, borderRadius: 8, objectFit: 'cover', border: '1px solid rgba(0,0,0,0.1)' }} />;
                            })}
                          </div>
                        );
                      })()}
                      <div style={{ fontSize: 10, marginTop: 4, opacity: 0.55, textAlign: 'right' }}>
                        {format(new Date(msg.sentAt), 'HH:mm', { locale: tr })}
                      </div>
                    </div>
                  </div>
                ))}
                {sending && (
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Bot size={13} color="white" />
                    </div>
                    <TypingDots />
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={e => { e.preventDefault(); handleSend(); }} style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8, background: 'white' }}>
                <input value={input} onChange={e => setInput(e.target.value)} placeholder="Bir müşteri gibi mesaj yazın..." style={{ flex: 1, borderRadius: 10, fontSize: 13 }} disabled={sending} />
                <button type="submit" disabled={sending || !input.trim()} style={{ width: 40, height: 40, borderRadius: 10, border: 'none', cursor: 'pointer', background: input.trim() ? 'linear-gradient(135deg, #6366f1, #3b82f6)' : '#f1f5f9', color: input.trim() ? 'white' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Send size={16} />
                </button>
              </form>
            </div>

            {/* Sağ Panel */}
            <div style={{ width: 220, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Hazır Senaryolar */}
              <div className="card" style={{ padding: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', marginBottom: 10, textTransform: 'uppercase' }}>Test Senaryoları</div>
                {quickMessages.map((msg, i) => (
                  <button key={i} onClick={() => handleSend(msg.text)} disabled={sending} style={{ width: '100%', padding: '8px 10px', border: '1px solid #f1f5f9', borderRadius: 8, background: '#fafbff', cursor: 'pointer', fontSize: 12, color: '#475569', textAlign: 'left', marginBottom: 5, display: 'flex', gap: 6, lineHeight: 1.4 }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#ede9fe'; e.currentTarget.style.borderColor = '#c4b5fd'; e.currentTarget.style.color = '#6366f1'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fafbff'; e.currentTarget.style.borderColor = '#f1f5f9'; e.currentTarget.style.color = '#475569'; }}>
                    <span>{msg.emoji}</span>{msg.text}
                  </button>
                ))}
              </div>

              {/* Analiz */}
              {lastUsage && (
                <div className="card" style={{ padding: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', marginBottom: 10, textTransform: 'uppercase' }}>Son Yanıt Analizi</div>
                  <div style={{ fontSize: 12, color: '#64748b', lineHeight: 2 }}>
                    <div>Model: <strong style={{ color: '#1e293b' }}>{MODELS.find(m => m.id === lastUsage.model)?.label || lastUsage.model}</strong></div>
                    <div>Yanıt: <strong style={{ color: '#1e293b' }}>{(lastUsage.responseMs / 1000).toFixed(1)}s</strong></div>
                  </div>
                </div>
              )}

              <div className="card" style={{ background: 'linear-gradient(135deg, #ede9fe, #dbeafe)', border: '1px solid #c4b5fd', padding: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', marginBottom: 6, textTransform: 'uppercase' }}>Bilgi</div>
                <div style={{ fontSize: 12, color: '#4c1d95', lineHeight: 1.6 }}>Test konuşması gerçek müşterilerden ayrı tutulur. Bot tüm otel ayarlarına göre yanıt verir.</div>
              </div>
            </div>
          </div>
        )}

        {/* ── TAB 5: İstatistikler ── */}
        {activeTab === '5' && (
          <div style={{ maxWidth: 900 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Kullanım İstatistikleri</div>
              <select value={statPeriod} onChange={e => setStatPeriod(e.target.value)} style={{ borderRadius: 8, border: '1.5px solid #e2e8f0', padding: '6px 12px', fontSize: 13, background: 'white', cursor: 'pointer' }}>
                <option value="7">Son 7 Gün</option>
                <option value="30">Son 30 Gün</option>
                <option value="90">Son 90 Gün</option>
              </select>
            </div>

            {!stats ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Yükleniyor...</div>
            ) : (
              <>
                {/* Stat Kartları */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                  <StatCard icon={MessageSquare} label="Toplam Mesaj"     value={stats.totalMessages.toLocaleString('tr')}    sub={`${stats.period} günde`} color="#6366f1" bg="#ede9fe" />
                  <StatCard icon={TrendingUp}   label="Cevaplama Oranı"  value={`%${stats.answerRate}`}                       color="#22c55e" bg="#dcfce7" />
                  <StatCard icon={Clock}        label="Ort. Yanıt Süresi" value={stats.avgResponseTimeMs > 0 ? `${(stats.avgResponseTimeMs / 1000).toFixed(1)}s` : '—'} color="#f59e0b" bg="#fef3c7" />
                  <StatCard icon={Bot}          label="Rezervasyon Talebi" value={stats.reservations}                         color="#3b82f6" bg="#dbeafe" />
                  <StatCard icon={DollarSign}   label="Toplam Maliyet"   value={`$${stats.totalCost}`}                        sub={`${(stats.totalInputTokens + stats.totalOutputTokens).toLocaleString('tr')} token`} color="#ec4899" bg="#fce7f3" />
                  <StatCard icon={Globe}        label="Konuşma"          value={stats.totalConversations.toLocaleString('tr')} color="#8b5cf6" bg="#f3e8ff" />
                </div>

                {/* Model Tablosu */}
                {Object.keys(stats.modelStats).length > 0 && (
                  <div className="card" style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 14 }}>Model Bazlı Maliyet</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          {['Model', 'Mesaj', 'Token', 'Maliyet'].map(h => (
                            <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(stats.modelStats).map(([model, data]) => (
                          <tr key={model} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1e293b' }}>{MODELS.find(m => m.id === model)?.label || model}</td>
                            <td style={{ padding: '10px 12px', color: '#64748b' }}>{data.messages}</td>
                            <td style={{ padding: '10px 12px', color: '#64748b' }}>{(data.inputTokens + data.outputTokens).toLocaleString('tr')}</td>
                            <td style={{ padding: '10px 12px', fontWeight: 700, color: '#1e293b' }}>${data.cost.toFixed(4)}</td>
                          </tr>
                        ))}
                        <tr style={{ background: '#f8fafc' }}>
                          <td style={{ padding: '10px 12px', fontWeight: 700, color: '#1e293b' }}>TOPLAM</td>
                          <td style={{ padding: '10px 12px', fontWeight: 700 }}>{stats.totalMessages}</td>
                          <td style={{ padding: '10px 12px', fontWeight: 700 }}>{(stats.totalInputTokens + stats.totalOutputTokens).toLocaleString('tr')}</td>
                          <td style={{ padding: '10px 12px', fontWeight: 700, color: '#6366f1' }}>${stats.totalCost}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Top SSS */}
                {stats.topFaqs?.length > 0 && (
                  <div className="card">
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 14 }}>En Çok Sorulan Sorular</div>
                    {stats.topFaqs.map((faq, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < stats.topFaqs.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                        <div style={{ width: 24, height: 24, borderRadius: 8, background: '#ede9fe', color: '#6366f1', fontWeight: 800, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</div>
                        <div style={{ flex: 1, fontSize: 13, color: '#1e293b' }}>{faq.question}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{faq.askedCount} kez</div>
                      </div>
                    ))}
                  </div>
                )}

                {stats.totalMessages === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8', background: 'white', borderRadius: 14, border: '1.5px dashed #e2e8f0' }}>
                    <BarChart2 size={32} color="#c4b5fd" style={{ marginBottom: 10 }} />
                    <div style={{ fontWeight: 700, color: '#475569', marginBottom: 4 }}>Henüz kullanım verisi yok</div>
                    <div style={{ fontSize: 13 }}>Bot kullanımı başladıkça burada istatistikler görünür.</div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
